import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle, ShieldCheck } from "@phosphor-icons/react";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate(user.role === "admin" ? "/dashboard" : "/pedidos", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await login(identifier, password);
      navigate(u.role === "admin" ? "/dashboard" : "/pedidos", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* left brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-zinc-950 p-12 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-20"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1574848296471-28f79a036f79?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200')", backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="relative self-start">
          <img src="/dualt-logo.png" alt="DUALT" className="h-10 w-auto object-contain" />
        </div>
        <div className="relative space-y-6 max-w-md">
          <h1 className="font-heading text-4xl font-bold text-white leading-tight">
            Emissão de pedidos, sem planilha.
          </h1>
          <ul className="space-y-3">
            {["Cadastro de clientes com busca por CNPJ", "Catálogo e tabelas de preços", "PDF do pedido e envio por e-mail"].map((t) => (
              <li key={t} className="flex items-center gap-3 text-zinc-300">
                <CheckCircle size={22} weight="fill" className="text-accent shrink-0" /> {t}
              </li>
            ))}
          </ul>
        </div>
        <p className="relative text-xs text-zinc-600">© 2026 DUALT — Sistema de Emissão de Pedidos</p>
      </div>

      {/* right form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-sm fade-in">
          <div className="lg:hidden font-heading font-extrabold text-3xl tracking-tight mb-8">
            DUAL<span className="text-accent">T</span>
          </div>
          <h2 className="font-heading text-2xl font-bold text-zinc-950">Acessar sistema</h2>
          <p className="text-sm text-muted-foreground mt-1 mb-8">Entre com seu usuário, e-mail ou CNPJ.</p>

          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Usuário / E-mail / CNPJ</Label>
              <Input
                data-testid="login-identifier"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="admin@dualt.com.br"
                className="h-12"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Senha</Label>
              <Input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-12"
                required
              />
            </div>
            {error && <p data-testid="login-error" className="text-sm text-destructive">{error}</p>}
            <Button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-accent hover:bg-accent/90 text-white text-base font-semibold"
            >
              {loading ? "Entrando..." : "Entrar"}
            </Button>
          </form>
          <p className="flex items-center gap-2 text-xs text-muted-foreground mt-8">
            <ShieldCheck size={16} className="text-accent" /> Acesso restrito a representantes e administração.
          </p>
        </div>
      </div>
    </div>
  );
}
